/**
 * Created by fijas on 4/17/17.
 */
import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {Observable} from 'rxjs';
import {Search} from '../../reports/contractor-awards/Search';
import {MajorPlayersSearch} from '../../reports/major-players/MajorPlayersSearch';
import {BudgetTotalsSearch} from '../../reports/budget-totals/BudgetTotalsSearch';
import {OpportunityMatrixSearch} from '../../reports/opportunity-matrix/OpportunityMatrixSearch';
import {ValueMatrixSearch} from 'app/reports/value-matrix/ValueMatrixSearch';
import { HttpClient, HttpParams } from '@angular/common/http';
import {CommonService} from './common.service';
import {QuarterlyReportSearch} from '../../reports/quarterly-reports/QuarterlyReportSearch';
import {MarketSearch} from '../../reports/market-awards/MarketSearch';
import {Contact} from '../../projects/Contact';
import {ReportBreakdownResponse, ReportExportRequest, ChartImageData} from '../models/report-data.interface';
import {StandardResponse} from '../../common/models/StandardResponse';

@Injectable()
export class ReportService {
    // URL to web api
    readonly reportsUrl: string;
    readonly generateUrl: string;

    constructor(private http: HttpClient, private commonService: CommonService) {
        this.reportsUrl = environment.apiBaseUrl + 'reports';
        this.generateUrl = environment.apiBaseUrl + 'generates';
    }

    getReportYears(from = 2005, to = 2027) {
        const years: any[] = [];
        for (let i = from; i <= to; i++) {
            years.push(i);
        }
        return years;
    }

    getContractorAwards(searchModel: Search, returnType = 'stats', page = 1) {
        let params = new HttpParams()
            .set('filter[from]', searchModel.yearFrom)
            .set('filter[to]', searchModel.yearTo)
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('returnType', returnType)
            .set('page', page);

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.regions.length > 0) {
            params = params.set('filter[regions]', convertedData.regions)
        }
        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get(this.reportsUrl + '/contractor-awards', {params: params, observe: 'response'});
    }

    getMarketAwards(searchModel: MarketSearch, returnType = 'stats', page = 1) {
        let params = new HttpParams()
            .set('filter[from]', searchModel.yearFrom)
            .set('filter[to]', searchModel.yearTo)
            .set('filter[type]', searchModel.type.toString())
            .set('filter[by]', searchModel.by.toString())
            .set('returnType', returnType)
            .set('page', page);

        const companies: any[] = [];
        for (const company of searchModel.companies) {
            companies.push(company.id);
        }

        if (companies.length > 0) {
            params =  params.set('filter[contactIds]', companies.join(','))
        }

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get(this.reportsUrl + '/market-awards', {params: params, observe: 'response'});
    }

    getContractorAwardsSummary(searchModel: Search, countryId: number) {
        let params = new HttpParams()
            .set('filter[from]', searchModel.yearFrom)
            .set('filter[to]', searchModel.yearTo)
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('filter[country_id]', countryId.toString())
            .set('filter[expected]', '1');

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get(this.reportsUrl + '/contractor-awards/summary', {params: params, observe: 'response'});
    }

    getContractorAwardsByCountry(searchModel: Search, year: number, countryId: number): Observable<ReportBreakdownResponse> {
        let params = new HttpParams()
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('year', year.toString())
            .set('country_id', countryId.toString());

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get<ReportBreakdownResponse>(this.reportsUrl + '/contractor-awards/by-country', {params: params});
    }

    getContractorAwardsByIndustry(searchModel: Search, year: number, countryId: number, industryId: number): Observable<ReportBreakdownResponse> {
        let params = new HttpParams()
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('year', year.toString())
            .set('country_id', countryId.toString())
            .set('industry_id', industryId.toString());

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get<ReportBreakdownResponse>(this.reportsUrl + '/contractor-awards/by-industry', {params: params});
    }

    getConstructionSpends(searchModel: Search, returnType = 'stats', page = 1) {
        let params = new HttpParams()
            .set('filter[from]', searchModel.yearFrom)
            .set('filter[to]', searchModel.yearTo)
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('returnType', returnType)
            .set('page', page);

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.regions.length > 0) {
            params = params.set('filter[regions]', convertedData.regions)
        }
        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get(this.reportsUrl + '/construction-spends', {params: params, observe: 'response'});
    }

    getMajorPlayers(searchModel: MajorPlayersSearch, contactId = 0, returnType = 'stats', page = 1) {
        let params = new HttpParams()
            .set('filter[from]', searchModel.from ? searchModel.getDate(searchModel.from) : '')
            .set('filter[to]', searchModel.to ? searchModel.getDate(searchModel.to) : '')
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[companyType]', searchModel.companyType.toString())
            .set('filter[dateType]', searchModel.dateType.toString())
            .set('filter[by]', searchModel.by.toString())
            .set('filter[limit]', searchModel.limit.toString())
            .set('contact_id', contactId.toString())
            .set('returnType', returnType)
            .set('page', page);

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        // TODO: type when API schema available (paginated response with observe: 'response')
        return this.http.get<any>(this.reportsUrl + '/major-players', {params: params, observe: 'response'});
    }

    getBudgetTotals(searchModel: BudgetTotalsSearch): Observable<Record<string, unknown>> {
        let params = new HttpParams()
           // .set('filter[statuses]', searchModel.statuses.join(','))
            .set('filter[by]', searchModel.by.toString());
        const statuses: any[] = [];
        for (const status of searchModel.statuses) {
            statuses.push(status['value']);
        }
        let statusString = '';
        if (statuses.length > 0) {
            statusString = statuses.join(',');
            params = params.set('filter[statuses]', statusString);
        }
        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        return this.http.get<Record<string, unknown>>(this.reportsUrl + '/budget-totals', {params: params});
    }

    getOpportunityMatrix(searchModel: OpportunityMatrixSearch): Observable<Record<string, unknown>> {
        let params = new HttpParams()
            .set('filter[execution]', searchModel.execution.toString())
            .set('filter[by]', searchModel.by.toString());

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }
        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }

        return this.http.get<Record<string, unknown>>(this.reportsUrl + '/opportunities', {params: params});
    }

    getValueMatrix(searchModel: ValueMatrixSearch): Observable<Record<string, unknown>> {
        let params = new HttpParams();

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }

        return this.http.get<Record<string, unknown>>(this.reportsUrl + '/values', {params: params});
    }

    contractorAwardsExport(type: number, searchModel: Search, report: string, randomString: string): Observable<StandardResponse> {
        const data: Partial<ReportExportRequest> = {};

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            data.countries = convertedData.countries
        }
        if (convertedData.industries.length > 0) {
            data.industries = convertedData.industries
        }
        if (convertedData.industryTypes.length > 0) {
            data.industryTypes = convertedData.industryTypes
        }

        data.from = searchModel.yearFrom;
        data.to = searchModel.yearTo;
        data.includeHold = searchModel.includeHold.toString();
        data.show = searchModel.show.toString();
        data.exportType = type.toString();
        data.report = report;
        data.channel_hash = randomString;

        return this.http.post<StandardResponse>(this.reportsUrl + '/exports', data);
    }

    marketAwardsExport(type: number, searchModel: MarketSearch, report: string, randomString: string): Observable<StandardResponse> {
        const data: Partial<ReportExportRequest> = {};

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            data.countries = convertedData.countries
        }
        if (convertedData.industries.length > 0) {
            data.industries = convertedData.industries
        }
        if (convertedData.industryTypes.length > 0) {
            data.industryTypes = convertedData.industryTypes
        }

        const contactIdArray: any[] = [];
        let contactIds: string | undefined;
        for (const company of searchModel.companies){
            contactIdArray.push(company.id);
        }

        if (contactIdArray.length > 0) {
            contactIds = contactIdArray.join(',');
        }

        data.from = searchModel.yearFrom;
        data.to = searchModel.yearTo;
        data.includeHold = searchModel.includeHold.toString();
        data.show = searchModel.show.toString();
        data.exportType = type.toString();
        data.report = report;
        data.contactIds = contactIds;
        data.channel_hash = randomString;

        return this.http.post<StandardResponse>(this.reportsUrl + '/exports', data);
    }

    constructionSpendsExport(type: number, searchModel: Search, report: string, randomString: string): Observable<StandardResponse> {
        const data: Partial<ReportExportRequest> = {};

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            data.countries = convertedData.countries
        }
        if (convertedData.industries.length > 0) {
            data.industries = convertedData.industries
        }
        if (convertedData.industryTypes.length > 0) {
            data.industryTypes = convertedData.industryTypes
        }

        data.from = searchModel.yearFrom;
        data.to = searchModel.yearTo;
        data.includeHold = searchModel.includeHold.toString();
        data.show = searchModel.show.toString();
        data.exportType = type.toString();
        data.report = report;
        data.channel_hash = randomString;

        return this.http.post<StandardResponse>(this.reportsUrl + '/exports', data);
    }

    majorPlayersExport(type: number, searchModel: MajorPlayersSearch, report: string, randomString: string): Observable<StandardResponse> {
        const data: Partial<ReportExportRequest> = {};

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            data.countries = convertedData.countries
        }
        if (convertedData.industries.length > 0) {
            data.industries = convertedData.industries
        }
        if (convertedData.industryTypes.length > 0) {
            data.industryTypes = convertedData.industryTypes
        }

        data.companyType = searchModel.companyType.toString();
        data.limit = searchModel.limit.toString();
        data.by = searchModel.by.toString();
        data.dateType = searchModel.dateType.toString();

        if (searchModel.from) {
            data.from = searchModel.getDate(searchModel.from);
        }
        if (searchModel.to) {
            data.to = searchModel.getDate(searchModel.to);
        }

        data.includeHold = searchModel.includeHold.toString();
        data.exportType = type.toString();
        data.report = report;
        data.channel_hash = randomString;

        return this.http.post<StandardResponse>(this.reportsUrl + '/exports', data);
    }

    getConstructionHighlights(): Observable<Record<string, unknown>> {
        return this.http.get<Record<string, unknown>>(this.reportsUrl + '/construction-highlights');
    }

    getConstructionSpendsByCountry(searchModel: Search, year: number, countryId: number): Observable<ReportBreakdownResponse> {
        let params = new HttpParams()
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('year', year.toString())
            .set('country_id', countryId.toString());

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get<ReportBreakdownResponse>(this.reportsUrl + '/construction-spends/by-country', {params: params});
    }

    getConstructionSpendsByIndustry(searchModel: Search, year: number, countryId: number, industryId: number): Observable<ReportBreakdownResponse> {
        let params = new HttpParams()
            .set('filter[includeHold]', searchModel.includeHold.toString())
            .set('filter[show]', searchModel.show.toString())
            .set('year', year.toString())
            .set('filter[from]', searchModel.yearFrom)
            .set('filter[to]', searchModel.yearTo)
            .set('country_id', countryId.toString())
            .set('industry_id', industryId.toString());

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.industries.length > 0) {
            params = params.set('filter[industries]', convertedData.industries)
        }
        if (convertedData.industryTypes.length > 0) {
            params = params.set('filter[industryTypes]', convertedData.industryTypes)
        }

        return this.http.get<ReportBreakdownResponse>(this.reportsUrl + '/construction-spends/by-industry', {params: params});
    }

    getQuarterlyReports(searchModel: QuarterlyReportSearch) {
        let params = new HttpParams()
            .set('filter[year]', searchModel.year.toString())
            .set('filter[quarter]', searchModel.quarter.toString());
        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            params = params.set('filter[countries]', convertedData.countries)
        }

        // TODO: type when API schema available (paginated response with observe: 'response')
        return this.http.get<any>(this.reportsUrl + '/quarterlies', {params: params, observe: 'response'});
    }

    generatePDF(searchModel: QuarterlyReportSearch, chartImageData: ChartImageData, label: string, randomString: string) {
        let countries: string | undefined;
        let quarter: number;
        let year: number;

        quarter = searchModel.quarter;
        year = searchModel.year;

        const convertedData = this.commonService.convertDataFromTree(searchModel);

        if (convertedData.countries.length > 0) {
            countries = convertedData.countries
        }
        const data = {
            type: 'pdf', for: 'quarterly', id: 1,
            countries: countries, quarter: quarter, year: year,
            monthImageData: chartImageData.month,
            countryImageData: chartImageData.country,
            industryImageData: chartImageData.industry,
            label: label,
            channel_hash: randomString
        };

        return this.http.post(this.generateUrl, data);
    }

    lookupCompanies(q: string, type): Observable<Contact[]> {
        return this.http.get<Contact[]>(this.reportsUrl + '/market-awards/lookup?type=' + type + '&q=' + q);
    }

}
